import { useEffect, useState } from "react";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { money, monthLabel } from "../lib/format";
import { Panel, Empty } from "./Widgets";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { COMPANIES } from "./Companies";

const sel = "h-8 rounded-md bg-[#0B0F17] border border-slate-700 text-xs px-2";

function useOwners() {
  const [o, setO] = useState({ agents: [], house: [] });
  useEffect(() => {
    Promise.all([api.get("/agents"), api.get("/settings")]).then(([a, s]) => setO({ agents: a.data, house: s.data.house_codes }));
  }, []);
  return o;
}

function AssignBar({ count, onAssign, busy }) {
  const { agents, house } = useOwners();
  const [target, setTarget] = useState("");
  return (
    <div className="flex flex-wrap items-center gap-2 p-4 border-b border-slate-800 bg-[#0d1320]" data-testid="unassigned-assign-bar">
      <span className="text-xs text-slate-400" data-testid="unassigned-selected-count">{count} selected · assign to</span>
      <select data-testid="unassigned-target" value={target} onChange={(e) => setTarget(e.target.value)} className={`${sel} w-56`}>
        <option value="">Choose agent or house…</option>
        <optgroup label="Agents">{agents.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.agent_code})</option>)}</optgroup>
        <optgroup label="House accounts">{house.map((h) => <option key={h} value={`house:${h}`}>House ({h})</option>)}</optgroup>
      </select>
      <Button data-testid="unassigned-assign-btn" size="sm" disabled={!count || !target || busy} onClick={() => onAssign(target)}
        className="bg-blue-500 hover:bg-blue-600 text-white"><UserPlus size={13} className="mr-1" />{busy ? "Assigning…" : "Assign"}</Button>
      <span className="text-[11px] text-slate-500">Applies from each merchant's first month; past payouts are recalculated. Its sheet code is remembered, so future merchants with that code go to the same owner.</span>
    </div>
  );
}

export function UnassignedMerchants() {
  const [company, setCompany] = useState("Priority Payments");
  const [q, setQ] = useState("");
  const [d, setD] = useState(null);
  const [checked, setChecked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [rulesKey, setRulesKey] = useState(0);
  const load = () => api.get("/merchants/unassigned", { params: company ? { company } : {} }).then((r) => { setD(r.data.merchants); setChecked([]); })
    .catch((e) => toast.error(errMsg(e)));
  useEffect(() => { setD(null); load(); }, [company]); // eslint-disable-line react-hooks/exhaustive-deps
  const n = q.trim().toLowerCase();
  const list = (d || []).filter((m) => !n || `${m.merchant_name} ${m.merchant_id} ${m.sheet_code}`.toLowerCase().includes(n));
  const toggle = (id) => setChecked((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  const assign = async (target) => {
    const picked = list.filter((m) => checked.includes(m.merchant_id));
    setBusy(true);
    try {
      const from = picked.map((m) => m.first_month).sort()[0];
      const { data } = await api.post("/merchants/reassign-bulk", { merchant_ids: picked.map((m) => m.merchant_id), target, from_month: from, remember_code: true });
      toast.success(`Assigned ${data.moved} merchant${data.moved === 1 ? "" : "s"} to ${data.to_owner}${data.remembered_codes ? ` · ${data.remembered_codes} sheet code${data.remembered_codes === 1 ? "" : "s"} remembered` : ""}`);
      load(); setRulesKey((k) => k + 1);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6">
    <Panel title={`Unassigned merchants${d ? ` · ${d.length}` : ""}`}>
      <div className="flex flex-wrap gap-2 p-4 border-b border-slate-800">
        <select data-testid="unassigned-company" value={company} onChange={(e) => setCompany(e.target.value)} className={sel}>
          {COMPANIES.map((c) => <option key={c} value={c}>{c}</option>)}<option value="">All companies</option>
        </select>
        <input data-testid="unassigned-search" value={q} onChange={(e) => { setQ(e.target.value); setChecked([]); }} placeholder="Search name, MID or sheet code" className={`${sel} w-64`} />
      </div>
      {d && d.length > 0 && <AssignBar count={checked.length} onAssign={assign} busy={busy} />}
      {!d ? <Empty>Loading…</Empty> : d.length === 0 ? <Empty>No unassigned {company || ""} merchants.</Empty> : list.length === 0 ? <Empty>No merchants match.</Empty> : (
        <table className="tbl w-full" data-testid="unassigned-table">
          <thead><tr><th className="w-8"><Checkbox data-testid="unassigned-select-all" checked={checked.length === list.length}
            onCheckedChange={(v) => setChecked(v ? list.map((m) => m.merchant_id) : [])} /></th>
            <th>Merchant</th><th>Company</th><th>Sheet code</th><th>First month</th><th className="!text-right">Months</th><th className="!text-right">Latest residual</th></tr></thead>
          <tbody>{list.map((m) => (
            <tr key={m.merchant_id} data-testid={`unassigned-row-${m.merchant_id}`} className="cursor-pointer" onClick={() => toggle(m.merchant_id)}>
              <td onClick={(e) => e.stopPropagation()}><Checkbox data-testid={`unassigned-check-${m.merchant_id}`} checked={checked.includes(m.merchant_id)} onCheckedChange={() => toggle(m.merchant_id)} /></td>
              <td className="text-slate-200">{m.merchant_name || "—"}<div className="font-mono text-[11px] text-slate-500">{m.merchant_id}</div></td>
              <td className="text-slate-400">{m.company}</td><td className="font-mono text-xs text-slate-500">{m.sheet_code || "—"}</td>
              <td className="font-mono text-xs">{monthLabel(m.first_month)}</td><td className="text-right font-mono">{m.months}</td>
              <td className="text-right font-mono">{money(m.last_residual)}</td>
            </tr>))}</tbody>
        </table>
      )}
    </Panel>
    <CodeRules key={rulesKey} />
    </div>
  );
}

function CodeRules() {
  const [rules, setRules] = useState(null);
  const load = () => api.get("/code-rules").then((r) => setRules(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const remove = async (r) => {
    try { await api.delete(`/code-rules/${r.id}`); toast.success(`Stopped auto-assigning ${r.code}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title={`Remembered sheet codes${rules ? ` · ${rules.length}` : ""}`}>
      {!rules ? <Empty>Loading…</Empty> : rules.length === 0 ? <Empty>No sheet codes remembered yet. Assigning a merchant above remembers its code.</Empty> : (
        <table className="tbl w-full" data-testid="code-rules-table">
          <thead><tr><th>Sheet code</th><th>Company</th><th>New merchants go to</th><th>Set</th><th /></tr></thead>
          <tbody>{rules.map((r) => (
            <tr key={r.id} data-testid={`code-rule-${r.id}`}>
              <td className="font-mono text-xs text-slate-200">{r.code}</td><td className="text-slate-400">{r.processor}</td>
              <td className="text-slate-200">{r.owner}</td><td className="text-[11px] text-slate-500">{r.at?.slice(0, 10)} · {r.by}</td>
              <td className="text-right"><Button data-testid={`code-rule-remove-${r.id}`} size="sm" variant="outline" onClick={() => remove(r)}>Remove</Button></td>
            </tr>))}</tbody>
        </table>
      )}
      <div className="px-4 py-2 text-[11px] text-slate-500">Removing a code only affects future uploads; merchants already assigned keep their owner.</div>
    </Panel>
  );
}
